/** @jsx jsx  */
import { Inline } from '@balance-web/inline';
import { jsx } from '@balance-web/core';
import { useTheme } from '@balance-web/theme';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { ChevronDownIcon } from '@balance-web/icon';
import { truncateText } from '@balance-web/utils';
import { useCallback, useRef } from 'react';
import { Tooltip } from '@balance-web/tooltip';

import { createTestId } from './utils';

type AppInfoProps = {
  /** Icon of the app. */
  appIconUrl: string;
  /** Name of the app. */
  appName: string;
  /** Name of the book being used. */
  bookName?: string;
  onClick?: () => void;
};
{
}
export const AppInfo = ({
  appIconUrl,
  appName,
  bookName,
  onClick,
}: AppInfoProps) => {
  const { semantic, sizing } = useTheme();
  const appLogoRef = useRef<HTMLImageElement>(null);

  const BookName = useCallback(() => {
    if (!bookName?.length) {
      return null;
    }

    const truncatedBookName = truncateText(bookName, 15);

    const bookNameText = (
      <Text
        color="textBody"
        css={{ lineHeight: 1 }}
        data={{ testid: createTestId('book-name') }}
        size="xsmall"
      >
        {truncatedBookName}
      </Text>
    );

    if (truncatedBookName === bookName) {
      return bookNameText;
    }

    return <Tooltip content={bookName}>{bookNameText}</Tooltip>;
  }, [bookName]);

  const AppName = useCallback(() => {
    const truncatedAppName = truncateText(appName, 15);

    const appNameText = (
      <Text
        css={{ lineHeight: 1 }}
        data={{ testid: createTestId('app-name') }}
        weight="bold"
      >
        {truncatedAppName}
      </Text>
    );

    if (truncatedAppName === appName) {
      return appNameText;
    }

    return <Tooltip content={appName}>{appNameText}</Tooltip>;
  }, [appName]);

  return (
    <Inline
      alignY="center"
      as={onClick && 'button'}
      borderRadius="small"
      css={
        onClick && {
          ':hover': {
            background: semantic.colour.surface.neutral.background1,
          },
          '&:focus-visible': {
            background: semantic.colour.surface.neutral.background1,
          },
          ':active': {
            background: semantic.colour.surface.neutral.background4,
          },
        }
      }
      data-testid="app-header-book-info"
      gap="small"
      paddingX="small"
      paddingY="xsmall"
      onClick={onClick}
    >
      <img
        ref={appLogoRef}
        alt={appName}
        css={{
          opacity: 0,
          transition: 'all 0.2s ease-in',
          height: sizing.small,
          width: sizing.small,
        }}
        src={appIconUrl}
        onLoad={() => {
          if (appLogoRef.current) {
            appLogoRef.current.style.opacity = '1';
          }
        }}
      />
      <Stack align="left" gap="xsmall">
        <AppName />
        <BookName />
      </Stack>
      {onClick ? <ChevronDownIcon color="textBody" size="small" /> : null}
    </Inline>
  );
};
